import styled from 'styled-components';
import { Icon } from '@logz-ui/styleguide';
import React, { useState, useCallback } from 'react';
import { useNavigationState } from 'ui/state/hooks';
import { navigationAppStateService } from 'ui/components/App/Navigation/app-state';

const STORAGE_KEY = 'minifier-position';

const StyledDiv = styled.div<{ top: number }>`
  position: absolute;
  right: 0;
  top: ${({ top }) => `${top}px`};
  background-color: ${({ theme }) => theme.colors.blue[600]};
  border-top-left-radius: 4px;
  border-bottom-left-radius: 4px;
  padding: 8px 4px 8px 4px;
  z-index: 2;
  cursor: pointer;
  opacity: 0.8;
  user-select: none;
`;

interface MinifierProps {
  onDragStateChange?: (isDragging: boolean) => void;
}

export const Minifier: React.FC<MinifierProps> = ({ onDragStateChange }) => {
  const { isMinified, setIsMinified } = useNavigationState();
  const [position, setPosition] = useState(() => {
    const savedPosition = localStorage.getItem(STORAGE_KEY);

    return savedPosition ? parseInt(savedPosition, 10) : 17;
  });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStartY, setDragStartY] = useState(0);
  const [hasDragged, setHasDragged] = useState(false);

  const handleToggleMinified = () => {
    if (hasDragged) {
      setHasDragged(false);

      return;
    }

    const newMinifiedState = !isMinified;

    setIsMinified(newMinifiedState);
    navigationAppStateService.setMinified(newMinifiedState);
  };

  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      setIsDragging(true);
      onDragStateChange?.(true);
      setHasDragged(false);
      setDragStartY(e.clientY - position);
    },
    [position, onDragStateChange],
  );

  const handleMouseMove = useCallback(
    (e: MouseEvent) => {
      if (isDragging) {
        const newPosition = e.clientY - dragStartY;
        const boundedPosition = Math.max(0, Math.min(window.innerHeight - 40, newPosition));

        setPosition(boundedPosition);
        localStorage.setItem(STORAGE_KEY, boundedPosition.toString());
        setHasDragged(true);
      }
    },
    [isDragging, dragStartY],
  );

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
    onDragStateChange?.(false);
  }, [onDragStateChange]);

  React.useEffect(() => {
    if (isDragging) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
    }

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, handleMouseMove, handleMouseUp]);

  return (
    <StyledDiv top={position} onClick={handleToggleMinified} onMouseDown={handleMouseDown}>
      <Icon color={'blue.100'} icon={isMinified ? 'chevron-right-regular' : 'chevron-left-regular'} size={10} />
    </StyledDiv>
  );
};
